import { SourceCode } from "@/components/CodeGen/SourceCode";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDeployment } from "@/lib/hooks/useDeployment";
import { useDeployments } from "@/lib/hooks/useDeployments";
import { useCurrentFileTree } from "@/lib/hooks/useFilesystem";
import { cn, formatDateISO, getStatusTextColor } from "@/lib/utils";
import {
  type FileTreeNode,
  buildFileTree,
  findFileInTree,
} from "@/lib/utils/file-tree-utils";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  Activity,
  ChevronDown,
  ChevronRight,
  Cloud,
  ExternalLink,
  FileCode,
  Folder,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { useMemo, useState } from "react";

interface ProjectDeploysTabProps {
  projectId: string;
}

export function ProjectDeploysTab({ projectId }: ProjectDeploysTabProps) {
  const navigate = useNavigate();
  const search = useSearch({ from: "/(projects)/projects/$projectId" });
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
    new Set(["src"]),
  );

  // Deployments data
  const {
    data: deploymentsData,
    isLoading: deploymentsLoading,
    refetch,
  } = useDeployments(projectId);

  // Files data (use current files for now, could be enhanced to show deployment-specific files)
  const { data: currentFiles, isLoading: filesLoading } =
    useCurrentFileTree(projectId);

  const deployments = deploymentsData?.deployments || [];

  // Auto-select latest deployment if none selected
  const latestDeployment = deployments[0]; // Deployments should be sorted by date
  const selectedFromSearch = deployments.find(
    (deployment) => deployment.id === search.deploymentId,
  )?.id;
  const activeDeploymentId = selectedFromSearch || latestDeployment?.id;
  const { data: selectedDeployment, isLoading: deploymentLoading } =
    useDeployment(projectId, activeDeploymentId || undefined);

  // Build file tree from current files
  const fileTree = useMemo(() => {
    if (!currentFiles) {
      return [];
    }
    return buildFileTree(currentFiles);
  }, [currentFiles]);

  // Deployment status color mapping using semantic colors
  const deploymentStatusColors = {
    complete: "text-success",
    error: "text-destructive",
    cancelled: "text-destructive",
    executing: "text-primary",
    pending: "text-warning",
  };

  const getStatusColor = (status: string) => {
    return getStatusTextColor(status, deploymentStatusColors);
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "complete":
        return "default" as const;
      case "error":
      case "cancelled":
        return "destructive" as const;
      case "executing":
        return "default" as const;
      case "pending":
        return "secondary" as const;
      default:
        return "outline" as const;
    }
  };

  // File helper functions
  const toggleFolder = (folderPath: string) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderPath)) {
      newExpanded.delete(folderPath);
    } else {
      newExpanded.add(folderPath);
    }
    setExpandedFolders(newExpanded);
  };

  const getFileContent = (filePath: string | null) => {
    if (!filePath || !fileTree) {
      return null;
    }
    const fileNode = findFileInTree(fileTree, filePath);
    return fileNode?.content || null;
  };

  const getFileLanguage = (filePath: string | null) => {
    if (!filePath) {
      return "text";
    }
    const ext = filePath.split(".").pop()?.toLowerCase();
    const languageMap: Record<string, string> = {
      ts: "typescript",
      js: "javascript",
      json: "json",
      md: "markdown",
      txt: "text",
      yml: "yaml",
      yaml: "yaml",
    };
    return languageMap[ext || ""] || "text";
  };

  const renderFileTree = (items: FileTreeNode[], depth = 0) => {
    return items.map((item) => {
      const itemPath = item.path;
      const isExpanded = expandedFolders.has(itemPath);
      const isSelected = selectedFile === itemPath;

      return (
        <div key={itemPath}>
          <button
            type="button"
            className={cn(
              "flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors w-full text-left",
              isSelected ? "bg-primary/10 text-primary" : "hover:bg-muted/50",
            )}
            style={{ paddingLeft: `${8 + depth * 16}px` }}
            onClick={() => {
              if (item.type === "folder") {
                toggleFolder(itemPath);
              } else {
                setSelectedFile(itemPath);
              }
            }}
            aria-pressed={isSelected}
          >
            {item.type === "folder" && (
              <>
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                )}
                <Folder className="w-4 h-4 text-primary flex-shrink-0" />
              </>
            )}
            {item.type === "file" && (
              <>
                <div className="w-4" />
                <FileCode className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              </>
            )}
            <span className="font-medium text-sm truncate">{item.name}</span>
            {item.type === "file" && (
              <span className="text-xs text-muted-foreground ml-auto flex-shrink-0">
                {item.size}
              </span>
            )}
          </button>

          {item.type === "folder" && item.children && isExpanded && (
            <div>{renderFileTree(item.children, depth + 1)}</div>
          )}
        </div>
      );
    });
  };

  const isLoading = deploymentsLoading || filesLoading;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with deployment selector and controls */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Cloud className="w-5 h-5 text-muted-foreground" />
                <span className="text-sm font-medium">Deployment:</span>
                <Select
                  value={activeDeploymentId || ""}
                  onValueChange={(value) => {
                    setSelectedFile(null);
                    navigate({
                      to: "/projects/$projectId",
                      params: { projectId },
                      search: (prev) => ({
                        ...prev,
                        tab: "deploys",
                        deploymentId: value,
                      }),
                    });
                  }}
                >
                  <SelectTrigger className="w-80">
                    <SelectValue placeholder="Select a deployment" />
                  </SelectTrigger>
                  <SelectContent>
                    {deployments.map((deployment) => (
                      <SelectItem key={deployment.id} value={deployment.id}>
                        <div className="flex items-center justify-between w-full">
                          <div className="flex items-center gap-2">
                            <div
                              className={cn(
                                "w-2 h-2 rounded-full",
                                deployment.status === "complete" &&
                                  "bg-success",
                                deployment.status === "error" &&
                                  "bg-destructive",
                                deployment.status === "executing" &&
                                  "bg-primary",
                                deployment.status === "pending" && "bg-warning",
                              )}
                            />
                            <span className="font-mono text-xs">
                              {deployment.id.slice(0, 8)}...
                            </span>
                          </div>
                          <span className="text-xs text-muted-foreground ml-4">
                            {formatDateISO(deployment.updatedAt)}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {deployments.length > 0 && (
                <Badge variant="outline">
                  {deployments.length} total deployments
                </Badge>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={deploymentsLoading}
            >
              <RefreshCw
                className={cn(
                  "w-4 h-4 mr-2",
                  deploymentsLoading && "animate-spin",
                )}
              />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Show message if no deployments */}
      {deployments.length === 0 ? (
        <Card>
          <CardContent className="flex items-center justify-center h-64 text-muted-foreground">
            <div className="text-center">
              <Cloud className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">No deployments found</p>
              <p className="text-sm">Deploy your first app to see it here</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-5 items-stretch gap-6">
          {/* Project Files */}
          <Card className="col-span-1 lg:col-span-2 xl:col-span-3 flex flex-col">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileCode className="w-5 h-5" />
                Project Files
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1">
              <div className="space-y-4 grid gap-4 lg:grid-cols-3 items-stretch h-full">
                {/* File Tree */}
                <div className="max-h-[500px] overflow-y-auto lg:col-span-1">
                  {fileTree.length === 0 ? (
                    <div className="flex items-center justify-center h-32 text-muted-foreground">
                      <div className="text-center">
                        <FileCode className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No files found</p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-1">{renderFileTree(fileTree)}</div>
                  )}
                </div>

                {/* Selected File Viewer */}
                <div className="border-l pl-4 lg:col-span-2">
                  {selectedFile ? (
                    <>
                      <div className="flex items-center gap-2 mb-2">
                        <FileCode className="w-4 h-4" />
                        <span className="font-medium text-sm">
                          {selectedFile}
                        </span>
                        <Badge variant="outline" className="text-xs">
                          {getFileLanguage(selectedFile)}
                        </Badge>
                      </div>
                      <SourceCode
                        key={getFileLanguage(selectedFile)}
                        content={getFileContent(selectedFile) || ""}
                        language={getFileLanguage(selectedFile)}
                        className="overflow-y-auto"
                      />
                    </>
                  ) : (
                    <div className="flex items-center justify-center h-full min-h-[300px] text-muted-foreground">
                      <div className="text-center">
                        <FileCode className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p className="text-lg font-medium mb-2">
                          No file selected
                        </p>
                        <p className="text-sm">
                          Choose a file from the tree to view its contents
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
          {/* Deployment Details */}
          <Card className="col-span-1 xl:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Deployment Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              {deploymentLoading ? (
                <div className="flex items-center justify-center h-[300px]">
                  <Loader2 className="w-8 h-8 animate-spin" />
                </div>
              ) : selectedDeployment ? (
                <div className="space-y-4">
                  {/* Deployment Header */}
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div>
                      <p className="font-medium">
                        Status:{" "}
                        <span
                          className={cn(
                            getStatusColor(
                              selectedDeployment.data.deployment.status,
                            ),
                          )}
                        >
                          {selectedDeployment.data.deployment.status}
                        </span>
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {formatDateISO(
                          selectedDeployment.data.deployment.createdAt,
                        )}
                      </p>
                    </div>
                    {selectedDeployment.data.deployment.url && (
                      <Button variant="outline" size="sm" asChild>
                        <a
                          href={selectedDeployment.data.deployment.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          View App
                        </a>
                      </Button>
                    )}
                  </div>

                  {/* Deployment Steps */}
                  <div className="space-y-3">
                    <h4 className="font-medium">Deployment Steps</h4>
                    <div className="max-h-[400px] overflow-y-auto space-y-3">
                      {selectedDeployment.data.deployment.steps.map((step) => (
                        <div
                          key={step.id}
                          className="border rounded-lg p-3 space-y-2"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Activity className="w-4 h-4 text-muted-foreground" />
                              <span className="font-medium">{step.name}</span>
                            </div>
                            <Badge variant={getStatusBadgeVariant(step.status)}>
                              {step.status}
                            </Badge>
                          </div>

                          {step.logs && (
                            <pre className="text-xs bg-muted p-2 rounded max-h-32 overflow-y-auto font-mono">
                              {step.logs}
                            </pre>
                          )}

                          <div className="flex items-center justify-between text-xs text-muted-foreground">
                            {step.startedAt && (
                              <span>
                                Started: {formatDateISO(step.startedAt)}
                              </span>
                            )}
                            {step.finishedAt && (
                              <span>
                                Finished: {formatDateISO(step.finishedAt)}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                  <div className="text-center">
                    <Cloud className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">
                      Select a deployment to view details
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
